import React, { useMemo, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'

import styles from './UpdatePlanExpirationDateForm.module.scss'
import { client } from '../../../../apollo'
import { UPDATE_ENTERPRISE_PLAN_EXPIRATION_DATE } from '../../../../graphql/enterprisePlan'
import { ENTERPRISE_PLAN_EXPIRATION_DATES } from '../../../../graphql/enterprisePlans'

type SubmitStatus = 'idle' | 'submitting' | 'success' | 'failed'

const PLACEHOLDER = '—'

const formatDate = (isoDateTime: string | null | undefined) =>
  isoDateTime ? new Date(isoDateTime).toISOString().slice(0, 10) : 'Not set'

export default function UpdatePlanExpirationDateForm() {
  const [selectedPlanId, setSelectedPlanId] = useState<string>('')
  const [newExpirationDate, setNewExpirationDate] = useState<string>('')
  const [status, setStatus] = useState<SubmitStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [updatedTo, setUpdatedTo] = useState<string | null>(null)

  const {
    data,
    loading: loadingPlans,
    error: plansError,
    refetch,
  } = useQuery(ENTERPRISE_PLAN_EXPIRATION_DATES, { client })

  const plans = useMemo(
    () =>
      [...(data?.enterprisePlanExpirationDates ?? [])].sort((a, b) =>
        a.orgName.localeCompare(b.orgName, undefined, {
          sensitivity: 'base',
        }),
      ),
    [data],
  )
  const selectedPlan = plans.find((plan) => String(plan.id) === selectedPlanId)
  const submitting = status === 'submitting'

  const [updateExpirationDate] = useMutation(
    UPDATE_ENTERPRISE_PLAN_EXPIRATION_DATE,
    {
      onCompleted: (result) => {
        const response = result.adminUpdateEnterprisePlanExpirationDate
        if (response.statusCode === 200) {
          setUpdatedTo(formatDate(response.planExpirationDate))
          setNewExpirationDate('')
          setStatus('success')
          refetch()
        } else {
          setError(response.error || 'An unknown error occurred')
          setStatus('failed')
        }
      },
      onError: (err) => {
        setError(err.message || 'Failed to update expiration date')
        setStatus('failed')
      },
      client,
    },
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedPlan) {
      return
    }
    setError(null)
    setStatus('submitting')

    try {
      await updateExpirationDate({
        variables: {
          enterprisePlanId: selectedPlan.id,
          planExpirationDate: newExpirationDate,
        },
      })
    } catch (_err: any) {
      // Error handling is done in onError callback
    }
  }

  let statusMessage: React.ReactNode = null
  if (status === 'submitting') {
    statusMessage = <span>Submitting request...</span>
  } else if (status === 'success') {
    statusMessage = (
      <span className={styles.success}>Success! Updated to {updatedTo}</span>
    )
  } else if (status === 'failed') {
    statusMessage = <span className={styles.error}>Failed ({error})</span>
  } else if (plansError) {
    statusMessage = (
      <span className={styles.error}>
        Failed to load enterprise plans ({plansError.message})
      </span>
    )
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div className={styles.field}>
        <label htmlFor="enterprisePlanId" className={styles.fieldLabel}>
          Enterprise Plan
        </label>
        <select
          id="enterprisePlanId"
          name="enterprisePlanId"
          value={selectedPlanId}
          onChange={(e) => {
            setSelectedPlanId(e.target.value)
            setNewExpirationDate('')
            setError(null)
            setStatus('idle')
          }}
          disabled={loadingPlans || submitting}
          required
        >
          <option value="">
            {loadingPlans ? 'Loading plans...' : 'Select a plan'}
          </option>
          {plans.map((plan) => (
            <option key={plan.id} value={plan.id}>
              {plan.orgName} (id {plan.id})
            </option>
          ))}
        </select>
      </div>

      <dl className={styles.info}>
        <dt className={styles.infoLabel}>Plan</dt>
        <dd className={styles.infoValue}>
          {selectedPlan ? (
            selectedPlan.orgName
          ) : (
            <span className={styles.placeholder}>{PLACEHOLDER}</span>
          )}
        </dd>
        <dt className={styles.infoLabel}>ID</dt>
        <dd className={styles.infoValue}>
          {selectedPlan ? (
            selectedPlan.id
          ) : (
            <span className={styles.placeholder}>{PLACEHOLDER}</span>
          )}
        </dd>
        <dt className={styles.infoLabel}>Current expiration date</dt>
        <dd className={styles.infoValue}>
          {selectedPlan ? (
            formatDate(selectedPlan.planExpirationDate)
          ) : (
            <span className={styles.placeholder}>{PLACEHOLDER}</span>
          )}
        </dd>
      </dl>

      <div className={styles.field}>
        <label htmlFor="planExpirationDate" className={styles.fieldLabel}>
          New Expiration Date
        </label>
        <input
          type="date"
          id="planExpirationDate"
          name="planExpirationDate"
          value={newExpirationDate}
          onChange={(e) => {
            setNewExpirationDate(e.target.value)
            setStatus('idle')
          }}
          disabled={!selectedPlan || submitting}
          required
        />
      </div>

      <div className={styles.submitRow}>
        <button
          type="submit"
          disabled={!selectedPlan || !newExpirationDate || submitting}
        >
          Submit
        </button>
        <div aria-live="polite">{statusMessage}</div>
      </div>
    </form>
  )
}
